/**
 * File dictionary.js
 *
 * Handles dynamic fetching of dictionary terms, and term search
 */

let controller;
const smallScreens = window.matchMedia("(max-width: 980px)");

let load = () => {
	if (!document.querySelector(".dictionary-main-content")) return;

	// handle initial anchor links
	const initialAnchor = location.hash;
	if (initialAnchor) scrollToAnchor(initialAnchor);

	/**
	 * Tabs
	 *
	 * We track which tabs have been loaded, so that only the first click will trigger network activity
	 * We use the History API to ensure back/fwd buttons work as users navigate through tabs
	 */
	let state = {
		"a-e": false,
		"f-j": false,
		"k-o": false,
		"p-t": false,
		"u-z": false,
	};

	Object.keys(state).forEach((tabKey) => {
		const button = document.querySelector(`.dictionary-nav-item[data-bs-target="#${tabKey}"]`);

		if (button.classList.contains("active")) {
			state[tabKey] = true;
			history.replaceState(tabKey, "");
		}

		button.addEventListener("click", (e) => {
			// active current tab and inactive rest
			const allTabs = document.querySelectorAll(".dictionary-nav-item");
			allTabs.forEach((item) => item.classList.remove("active"));
			button.classList.add("active");

			// active current tab-page and inactive rest
			const allTabPanes = document.querySelectorAll(".tab-pane");
			allTabPanes.forEach((tabPane) => tabPane.classList.remove("active", "show"));
			document.getElementById(tabKey).classList.add("active", "show");

			if (e.isTrusted) {
				history.pushState(tabKey, "", `${location.pathname}?tab=${tabKey}`);
			}

			if (!state[tabKey]) {
				loadTerms(tabKey).then((success) => {
					state[tabKey] = success;
				});
			}

			const dictionarySection = document.querySelector(".dictionary-section");

			if (dictionarySection.classList.contains("sticky")) {
				scrollToAnchor("#dictionary-filter-anchor");
			}
		});
	});

	// Search box toggle
	const searchButton = document.querySelector(".dictionary-search-button"),
		box = document.querySelector(".dictionary-search-box"),
		searchInput = document.querySelector(".dictionary-search-input");

	searchButton.addEventListener("click", () => {
		box.classList.toggle("show");
		searchInput.focus();
	});

	// hide on outside click
	document.addEventListener("click", function (event) {
		const isClickInside = box.contains(event.target) || searchButton.contains(event.target);

		if (!isClickInside) {
			box.classList.remove("show");
		}
	});

	// Mobile stickiness
	const dictionaryTabs = document.querySelector(".dictionary-section");
	if (smallScreens.matches) makeSticky(dictionaryTabs);

	// Perform search
	const input = box.querySelector("input"),
		results = box.querySelector(".dictionary-search-results"),
		loading = box.querySelector(".dictionary-search-loading"),
		emptyMsg = box.querySelector(".dictionary-search-empty"),
		clearToggles = box.querySelectorAll("[data-clear]"),
		showLoading = () => (loading.style.display = "flex"),
		hideLoading = () => (loading.style.display = "none"),
		showEmpty = () => (emptyMsg.style.display = "block"),
		hideEmpty = () => (emptyMsg.style.display = "none"),
		showClear = () => (clearToggles[0].style.display = "inline-block"),
		hideClear = () => (clearToggles[0].style.display = "none"),
		reset = () => {
			hideLoading();
			hideEmpty();
			hideClear();
			results.innerHTML = "";
		};

	input.addEventListener("input", (e) => {
		reset();

		const { value } = e.currentTarget;
		if (value.length) showClear();
		if (value.length > 1) {
			showLoading();
			performSearch(value).then((data) => {
				if (data.length) {
					hideEmpty();
					results.innerHTML = data
						.map(
							(result) => `
								<a
									href="#${result.anchor}"
									data-tab=${result.block.toLowerCase()}
								>
									${result.term}
								</a>
							`,
						)
						.join("");
				} else {
					showEmpty();
					results.innerHTML = "";
				}
				hideLoading();
			});
		}
	});

	clearToggles.forEach((toggle) =>
		toggle.addEventListener("click", () => {
			reset();
			input.value = "";
			input.focus();
		}),
	);

	// Click search result
	document.body.addEventListener("click", (e) => {
		if (!!e.target.closest(".dictionary-search-results")) {
			e.preventDefault();
			const { tab } = e.target.dataset,
				tabEl = document.querySelector(`[data-bs-target="#${tab}"]`),
				anchor = e.target.getAttribute("href");
			if (tabEl)
				tabEl.dispatchEvent(
					new MouseEvent("click", {
						view: window,
						bubbles: true,
						cancelable: false,
					}),
				);

			if (anchor) scrollToAnchor(anchor);

			history.pushState(tab, "", `${location.pathname}?tab=${tab}${anchor}`);
		}
	});

	// Handle synthetic history events
	window.addEventListener("popstate", ({ state }) => {
		const tabEl = document.querySelector(`.dictionary-nav-item[data-bs-target="#${state}"]`);
		const anchor = location.hash;

		if (tabEl) {
			tabEl.dispatchEvent(
				new MouseEvent("click", {
					view: window,
					bubbles: true,
					cancelable: false,
				}),
			);
		}

		if (anchor) scrollToAnchor(anchor);
	});
};

/**
 * Functions
 */
function loadTerms(tabKey) {
	return new Promise((resolve) => {
		if (!tabKey) {
			console.warn("Cannot load terms without specifying an alphabet block");
			resolve(false);
		}

		fetch(`/wp-json/stax/v1/dictionary?block=${tabKey}`, {
			method: "GET",
			headers: {
				Accept: "text/html, application/json",
			},
		}).then((response) => {
			if (!response.ok) {
				response.json().then((err) => console.error(err));
				resolve(false);
			}

			response.text().then((template) => {
				template = template.replace('""', "");

				const target = document.querySelector(`.tab-pane#${tabKey}`);
				target.innerHTML = template.length
					? template
					: `
						<p style="padding:20px">No terms found</p>
					`;
				resolve(true);
			});
		});
	});
}

function performSearch(term) {
	// abort previous search if results still pending
	if (controller) controller.abort();
	controller = new AbortController();

	return new Promise((resolve) => {
		fetch(`/wp-json/stax/v1/dictionary-search?q=${term}`, {
			signal: controller.signal,
		})
			.then((response) => {
				if (!response.ok) {
					response.json().then((err) => console.error(err));
					resolve([]);
				}

				response.json().then((results) => resolve(results));
			})
			.catch((err) => {
				if (err.name !== "AbortError") console.error(err);
			});
	});
}

function scrollToAnchor(anchor) {
	requestAnimationFrame(() => {
		const loop = setInterval(() => {
			const target = document.querySelector(anchor);
			if (target) {
				clearInterval(loop);
				let headerHeight = document.querySelector("#masthead").clientHeight;
				let dictionaryTabs = document.querySelector(".dictionary-section");
				if (smallScreens.matches) headerHeight += dictionaryTabs.clientHeight;
				target.style.top = `-${headerHeight + 12}px`;
				target.scrollIntoView({
					behavior: "smooth",
					block: "start",
					inline: "nearest",
				});

				const accordion = target.nextElementSibling;
				if (!accordion.classList.contains("is-open")) {
					accordion.dispatchEvent(
						new MouseEvent("click", {
							view: window,
							bubbles: true,
							cancelable: false,
						}),
					);
				}
			}
		}, 300);
	});
}

function makeSticky(el) {
	let stuck = false;
	const header = document.querySelector("#masthead");

	window.addEventListener("scroll", () => {
		const threshold = header.getBoundingClientRect().bottom;
		if (!stuck && el.getBoundingClientRect().top <= threshold) {
			stuck = true;
			const clone = el.cloneNode(true);
			clone.dataset.tabClone = true;
			el.classList.add("sticky");
			el.insertAdjacentElement("afterend", clone);
		}
		if (stuck) {
			const clone = document.querySelector("[data-tab-clone]");
			if (clone.getBoundingClientRect().top > threshold) {
				stuck = false;
				clone.remove();
				el.classList.remove("sticky");
			}
		}
	});
}

document.addEventListener("DOMContentLoaded", function () {
	load();
});
